import { Divider, Grid, Theme, Typography } from '@mui/material';
import { AcceptenceStatus, ApprovalStatus } from 'app/models/Placement/PlacementMods';
import { selectUser } from 'oidc/user.selectors';
import { isPayrollSpecialist, isReadOnly, isRecruitment } from 'oidc/userRoles';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentModDetails, selectExpandedModsDialog, selectIsEditExistingMod, selectIsApprovedTravelerBonus, selectIsApprovedNonTravelerBonus } from 'store/redux-store/placement-mods/selectors';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { makeStyles } from 'tss-react/mui';
import { ApprovalMethod } from './ApprovalMethod';
import { ApprovedBy } from './ApprovedBy';
import { ApprovedOn } from './ApprovedOn';
import { BillingAmount } from './BillingAmount';
import { BillingType } from './BillingType';
import { BonusAmount } from './BonusAmount';
import { BonusPaid } from './BonusPaid';
import { BonusType } from './BonusType';
import { DueDate } from './DueDate';
import { EffectiveEndDate } from './EffectiveEndDate';
import { EffectiveStartDate } from './EffectiveStartDate';
import { ForfeitBonus } from './ForfeitBonus';
import { Notes } from './Notes';
import { NumberOfWeeks } from './NumberOfWeeks';
import { Payer } from './Payer';
import { PaymentType } from './PaymentType';
import { RequiredHours } from './RequiredHours';
import { ShortHoursType } from './ShortHoursType';
import { useReadOnly } from 'oidc/userRoles';

const useStyles = makeStyles<{ isExpanded?: boolean }>()((theme: Theme, { isExpanded }) => ({
  typeSection: {
    minWidth: '550px',
    maxWidth: '550px',
  },
  dateSection: {
    maxWidth: isExpanded ? '100%' : '450px',
  },
  notesSectoin: {
    minWidth: '550px',
    maxWidth: '800px',
    marginTop: '20px',
  },
  approveSection: {
    minWidth: '550px',
    maxWidth: '550px',
  },
  devider: {
    marginTop: '20px',
    marginBottom: '25px',
  },
  dateGrid: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  checkboxPadding: {
    paddingTop: '15px',
    display: 'flex',
  },
  headings: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#333333',
  },
}));

export const BonusMod = () => { 
  const dispatch = useDispatch();
  const isEditExistingMod = useSelector(selectIsEditExistingMod); 
  const { readOnly } = useReadOnly();
  const isExpanded = useSelector(selectExpandedModsDialog);
  const { t } = useTranslation();
  const { classes } = useStyles({ isExpanded });
  const [disableNagativeOrVoid, setDisableNagativeOrVoid] = useState<boolean>(false);
  const currentModDetails = useSelector(selectCurrentModDetails);
  // Get current placement status to access activityTypeId
  const { currentPlacementStatus } = useSelector(selectPlacementStatus);
  const currentActivityTypeId = currentPlacementStatus?.activityTypeId;
  const currentActivityStatusId = currentPlacementStatus?.activityStatusId;
  // Get the states from Redux for local use
  const isApprovedTravelerState = useSelector(selectIsApprovedTravelerBonus);
  const isApprovedNonTravelerState = useSelector(selectIsApprovedNonTravelerBonus);
  const [isApprovalStatusEditable, setIsApprovalStatusEditable] = useState<boolean>(false);
  const [isFieldReadOnly, setIsFieldReadOnly] = useState<boolean>(false);
  const [isRadioButtonEditable, setIsRadioButtonEditable] = useState<boolean>(false);
  const [isAcceptanceStatusEditable, setIsAcceptanceStatusEditable] = useState<boolean>(false);
  // Check if the bonus modification is approved
  const isApproved = currentModDetails?.approvalStatusId === ApprovalStatus.Approved;
  const appUser = useSelector(selectUser);

  const getIsTravelerOption = () => {
    if (currentActivityTypeId === 1 && (currentActivityStatusId === 2 || currentActivityStatusId === 3
       || currentActivityStatusId === 6 || currentActivityStatusId === 8 || currentActivityStatusId === 16)) {
        return true;
    }
    if (currentActivityTypeId === 3 && (currentActivityStatusId === 9 || currentActivityStatusId === 10
       || currentActivityStatusId === 11 || currentActivityStatusId === 12)) {
        return true;
    }
    if (currentActivityTypeId === 4 && (currentActivityStatusId === 13)) {
        return true;
    }
    return false
  }

  const getIsNonTravelerOption = () => {
    if (currentActivityTypeId === 4 && (currentActivityStatusId === 14 || currentActivityStatusId === 15
        || currentActivityStatusId === 17 || currentActivityStatusId === 18)) {
        return true;
    }
    if (currentActivityTypeId === 5 && (currentActivityStatusId === 23 || currentActivityStatusId === 24
        || currentActivityStatusId === 25 || currentActivityStatusId === 26
        || currentActivityStatusId === 27 || currentActivityStatusId === 29)) {
        return true;
    }
    return false   
  }
  // Check if this is a traveler or non-traveler bonus mod
  const isTravelerOption = getIsTravelerOption();
  const isNonTravelerOption = getIsNonTravelerOption();
  
  useEffect(() => {
    if (readOnly) {
      setIsFieldReadOnly(true);
    } else {
      if ((isApprovedTravelerState || isApprovedNonTravelerState) && isEditExistingMod) {
        setIsFieldReadOnly(true);
      } else {
        setIsFieldReadOnly(false);
      }
    }
  }, [readOnly, isApprovedTravelerState, isApprovedNonTravelerState, isEditExistingMod]);

  useEffect(() => {
    if (readOnly) {
      setIsRadioButtonEditable(false);
    } else {
      if (!isApproved) {
        setIsRadioButtonEditable(true);
      } else if ((isApprovedNonTravelerState) && isEditExistingMod) {
        setIsRadioButtonEditable(true);
      } else {
        setIsRadioButtonEditable(false);
      }
    }
  }, [readOnly, isApprovedNonTravelerState, isEditExistingMod, isApproved]);  
  
  useEffect(() => {
    if (readOnly) {
      setIsAcceptanceStatusEditable(false);
    } else {
      if (isApprovedNonTravelerState) {
        setIsAcceptanceStatusEditable(false);
      } else {
        setIsAcceptanceStatusEditable(true);
      }
    }
  }, [readOnly, isApprovedNonTravelerState, isEditExistingMod]);
  
  // Dispatch to Redux when isAcceptanceStatusEditable changes
  useEffect(() => {
    dispatch(placementModsAction.setAcceptanceStatusEditable(isAcceptanceStatusEditable));
  }, [isAcceptanceStatusEditable, dispatch]);
  
  useEffect(() => {
    if (readOnly) {
      setIsApprovalStatusEditable(false);
    } else {
      if (isApprovedTravelerState) {
        setIsApprovalStatusEditable(false);
      } else {
        setIsApprovalStatusEditable(true);
      }
    }
  }, [readOnly, isApprovedTravelerState, isEditExistingMod]);
  
  // Dispatch to Redux when isApprovalStatusEditable changes
  useEffect(() => {
    dispatch(placementModsAction.setApprovalStatusEditable(isApprovalStatusEditable));
  }, [isApprovalStatusEditable, dispatch]);
    
  // Use Redux to manage approved states
  useEffect(() => {
    const isApprovedTraveler = isApproved && isTravelerOption;
    dispatch(placementModsAction.setIsApprovedTravelerState(isApprovedTraveler));
  }, [isApproved, isTravelerOption, dispatch]);

  useEffect(() => {
    const isApprovedNonTraveler = isApproved && isNonTravelerOption;
    dispatch(placementModsAction.setIsApprovedNonTravelerState(isApprovedNonTraveler));
  }, [isApproved, isNonTravelerOption, dispatch]);

  useEffect(() => {
    if (
      currentModDetails.isNegativeStatus ||
      currentModDetails.voidStatus ||
      isReadOnly(appUser?.userInfo?.roles || [])
    ) {
      setDisableNagativeOrVoid(true);
    } else if (isRecruitment(appUser?.userInfo?.roles || [])) {
      setDisableNagativeOrVoid(false);
    } else if (
      isPayrollSpecialist(appUser?.userInfo?.roles || []) &&
      currentModDetails.isBookingApprovedOrGreater === true &&
      currentModDetails.acceptanceStatusId === AcceptenceStatus.Accepted &&
      currentModDetails.approvalStatusId === ApprovalStatus.Approved
    ) {
      setDisableNagativeOrVoid(false);
    } else {
      setDisableNagativeOrVoid(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={classes.typeSection}>
        <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={4}>
          <Grid item xs={7}>
            <BonusType readOnly={isFieldReadOnly} />
          </Grid>
          <Grid item xs={5}>
            <BonusAmount readOnly={isFieldReadOnly} />
          </Grid>

          <Grid item xs={7}>
            <PaymentType readOnly={isFieldReadOnly} />
          </Grid>
          <Grid item xs={5}>
            <Payer readOnly={isFieldReadOnly} />
          </Grid>

          <Grid item xs={7}>
            <BillingType readOnly={isFieldReadOnly} />
          </Grid>
          <Grid item xs={5}>
            <BillingAmount readOnly={isFieldReadOnly} />
          </Grid>

          <Grid item xs={7}>
            <ShortHoursType readOnly={isFieldReadOnly} />
          </Grid>
          <Grid item xs={5}>
            <DueDate readOnly={isFieldReadOnly} />
          </Grid>
        </Grid>
      </div>
      <Grid className={classes.devider}>
        <Divider />
      </Grid>
      <div className={classes.dateSection}>
        <Grid container spacing={4} className={classes.dateGrid}>
          <Grid item xs={isExpanded ? 3 : 6}>
            <EffectiveStartDate readOnly={isFieldReadOnly} />
          </Grid>
          <Grid item xs={isExpanded ? 3 : 6}>
            <EffectiveEndDate readOnly={isFieldReadOnly} />
          </Grid>
          <Grid item xs={isExpanded ? 3 : 6}>
            <NumberOfWeeks readOnly={isFieldReadOnly} />
          </Grid>
          <Grid item xs={isExpanded ? 3 : 6}>
            <RequiredHours readOnly={isFieldReadOnly} />
          </Grid>
        </Grid>
      </div>
      <div className={classes.notesSectoin}>
        <Grid container className={classes.dateGrid} columnSpacing={1} rowSpacing={4}>
          <Grid item xs={6}>
            <Notes readOnly={isFieldReadOnly} />
          </Grid>
          <Grid item xs={6}>
            <div className={classes.checkboxPadding}>
              <BonusPaid disableNagativeOrVoid={disableNagativeOrVoid} isRadioButtonEditable={isRadioButtonEditable} />{' '}
              <ForfeitBonus disableNagativeOrVoid={disableNagativeOrVoid} isRadioButtonEditable={isRadioButtonEditable} />
            </div>
          </Grid>
        </Grid>
      </div>
      <Grid className={classes.devider}>
        <Divider />
      </Grid>
      <div className={classes.approveSection}>
        <div style={{ marginTop: '-5px', marginBottom: '15px' }}>
          <Typography component="span" className={classes.headings}>{`${t(
            'placement.placementMods.clientApproval',
          )}`}</Typography>
        </div>
        <Grid container spacing={2} className={classes.dateGrid}>
          <Grid item xs={4}>
            <ApprovedBy readOnly={isFieldReadOnly} />
          </Grid>
          <Grid item xs={4}>
            <ApprovalMethod readOnly={isFieldReadOnly} />
          </Grid>
          <Grid item xs={4}>
            <ApprovedOn readOnly={isFieldReadOnly} />
          </Grid>
        </Grid>
      </div>
    </>
  );
};
